import { ReactElement, useEffect, useRef } from "react";
import dayjs from "dayjs";
import "dayjs/locale/ja";
dayjs.locale("ja");
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  Viewer,
  Worker,
  LocalizationMap,
  SpecialZoomLevel,
} from "@react-pdf-viewer/core";
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import {
  MoreActionsPopover,
  ToolbarProps,
  ToolbarSlot,
} from "@react-pdf-viewer/toolbar";
import { SearchIcon } from "@react-pdf-viewer/search";
import jp_JP from "@react-pdf-viewer/locales/lib/jp_JP.json";
import { TypeCommon } from "models/common";
import SearchSidebar from "components/search-side-bar";

const BaseModalPreviewPdf = ({
  openModal,
  handleCloseModal,
  url,
}: {
  openModal: boolean;
  handleCloseModal: () => void;
  url: string;
}): ReactElement => {
  const containerRef = useRef<TypeCommon>(null);
  const renderToolbar = (
    Toolbar: (props: ToolbarProps) => ReactElement,
  ): TypeCommon => (
    <Toolbar>
      {(toolbarSlot: ToolbarSlot): TypeCommon => {
        const {
          CurrentPageInput,
          Download,
          EnterFullScreen,
          GoToNextPage,
          GoToPreviousPage,
          NumberOfPages,
          Open,
          Print,
          SwitchTheme,
          Zoom,
          ZoomIn,
          ZoomOut,
        } = toolbarSlot;

        return (
          <div
            className="rpv-toolbar"
            role="toolbar"
            aria-orientation="horizontal"
          >
            <div className="rpv-toolbar__left margin-previous">
              <div className="rpv-core__display--hidden rpv-core__display--block-small">
                <div className="rpv-toolbar__item">
                  <GoToPreviousPage />
                </div>
              </div>
              <div className="rpv-toolbar__item">
                <CurrentPageInput />{" "}
                <span className="rpv-toolbar__label">
                  / <NumberOfPages />
                </span>
              </div>
              <div className="rpv-core__display--hidden rpv-core__display--block-small">
                <div className="rpv-toolbar__item">
                  <GoToNextPage />
                </div>
              </div>
            </div>
            <div className="rpv-toolbar__center">
              <div className="rpv-toolbar__item">
                <ZoomOut />
              </div>
              <div className="rpv-core__display--hidden rpv-core__display--block-small">
                <div className="rpv-toolbar__item">
                  <Zoom />
                </div>
              </div>
              <div className="rpv-toolbar__item">
                <ZoomIn />
              </div>
            </div>
            <div className="rpv-toolbar__right">
              <div className="rpv-core__display--hidden rpv-core__display--block-medium">
                <div className="rpv-toolbar__item">
                  <SwitchTheme />
                </div>
              </div>
              <div className="rpv-core__display--hidden rpv-core__display--block-medium">
                <div className="rpv-toolbar__item">
                  <EnterFullScreen />
                </div>
              </div>
              <div className="rpv-core__display--hidden rpv-core__display--block-medium">
                <div className="rpv-toolbar__item">
                  <Open />
                </div>
              </div>
              <div className="rpv-core__display--hidden rpv-core__display--block-medium">
                <div className="rpv-toolbar__item">
                  <Download />
                </div>
              </div>
              <div className="rpv-core__display--hidden rpv-core__display--block-medium">
                <div className="rpv-toolbar__item">
                  <Print />
                </div>
              </div>
              <div className="rpv-toolbar__item">
                <MoreActionsPopover toolbarSlot={toolbarSlot} />
              </div>
            </div>
          </div>
        );
      }}
    </Toolbar>
  );
  const newPlugin = defaultLayoutPlugin({
    renderToolbar,
    sidebarTabs: (defaultTabs) =>
      [
        {
          content: (
            <SearchSidebar
              searchPluginInstance={
                newPlugin.toolbarPluginInstance.searchPluginInstance
              }
            />
          ),
          icon: <SearchIcon />,
          title: "検索",
        },
      ].concat(defaultTabs),
  });

  const { toolbarPluginInstance } = newPlugin;
  const { zoomPluginInstance } = toolbarPluginInstance;
  const { zoomTo } = zoomPluginInstance;

  const LEVELS = [
    0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1, 1.1, 1.3, 1.5, 1.7, 1.9,
    2.1, 2.4, 2.7, 3.0, 3.3, 3.7, 4.1, 4.6, 5.1, 5.7, 6.3, 7.0, 7.7, 8.5, 9.4,
    10,
  ];

  const increase = (currentLevel: TypeCommon): number => {
    const found = LEVELS.find((item) => {
      return item > currentLevel;
    });
    return found || currentLevel;
  };

  const decrease = (currentLevel: TypeCommon): number => {
    const found = LEVELS.findIndex((item) => {
      return item >= currentLevel;
    });
    return found === -1 || found === 0 ? currentLevel : LEVELS[found - 1];
  };

  useEffect(() => {
    const handleClick = (): void => {
      const moreActions = document.querySelectorAll(
        '[aria-controls="rpv-core__popver-body-toolbar-more-actions"]',
      );
      if (moreActions) {
        const scrollMode = document.querySelectorAll(
          '[data-testid="scroll-mode__page-menu"]',
        )[0];
        if (scrollMode && scrollMode?.children) {
          scrollMode.children[1].innerHTML = "ページスクロール";
        }

        const singleMenu = document.querySelectorAll(
          '[data-testid="view-mode__single-menu"]',
        )[0];
        if (singleMenu && singleMenu?.children) {
          singleMenu.children[1].innerHTML = "シングルページ";
        }

        const dualMenu = document.querySelectorAll(
          '[data-testid="view-mode__dual-menu"]',
        )[0];
        if (dualMenu && dualMenu?.children) {
          dualMenu.children[1].innerHTML = "デュアルページ";
        }

        const coverMenu = document.querySelectorAll(
          '[data-testid="view-mode__dual-cover-menu"]',
        )[0];
        if (coverMenu && coverMenu?.children) {
          coverMenu.children[1].innerHTML = "デュアルページとカバー";
        }

        const downloadMenu = document.querySelectorAll(
          '[data-testid="get-file__download-menu"]',
        );
        if (downloadMenu && downloadMenu[0]) {
          (downloadMenu[0] as HTMLElement).style.display = "none";
        }
      }
    };

    const handleWheel = (event: TypeCommon): void => {
      if (event.ctrlKey) {
        event.preventDefault();
        event.stopImmediatePropagation();
        const elements = document.getElementsByClassName(
          "rpv-zoom__popover-target-scale--ltr",
        );
        if (event?.deltaY > 0) {
          if (elements.length > 0) {
            const firstElement = elements[0];
            const textContent = firstElement.textContent
              ? firstElement.textContent?.replace("%", "")
              : "0";
            const decreaseZoom = decrease(Number(textContent) / 100);
            zoomTo(decreaseZoom);
          }
        }
        if (event?.deltaY < 0) {
          if (elements.length > 0) {
            const firstElement = elements[0];
            const textContent = firstElement.textContent
              ? firstElement.textContent?.replace("%", "")
              : "0";
            const increaseZoom = increase(Number(textContent) / 100);
            zoomTo(increaseZoom);
          }
        }
      }
    };

    window?.addEventListener("click", handleClick);

    containerRef?.current?.addEventListener("wheel", handleWheel, {
      capture: true,
      passive: false,
    });

    return () => {
      window?.removeEventListener("click", handleClick);
      containerRef?.current?.removeEventListener("wheel", handleWheel);
    };
  }, []);
//   const handleDownload = async (): Promise<void> => {
//     const link = document.createElement("a");
//     link.href = url ?? "";
//     link.setAttribute("download", "file.pdf");
//     document.body.appendChild(link);
//     link.click();
//     link.parentNode?.removeChild(link);
//   };
  return (
    <Dialog
      open={openModal}
      onClose={handleCloseModal}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: {
            xs: "90%",
            // sm: "90%",
            // md: "65%",
          },
          height: "95vh",
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle
        sx={{
          textAlign: "left",
          pt: 3,
          pb: 1,
          position: "sticky",
          top: 0,
          zIndex: 10,
          background: "white",
        }}
      >
        <Typography variant="h5" component="span" fontWeight="bold">
          PDFプレビュー
        </Typography>
        <IconButton
          edge="end"
          color="inherit"
          onClick={handleCloseModal}
          aria-label="close"
          sx={{ position: "absolute", right: 20, top: 15 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <div className="pdf-container w-full h-full" ref={containerRef}>
          <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
            <Viewer
              defaultScale={SpecialZoomLevel.PageFit}
              fileUrl={url ?? ""}
              plugins={[newPlugin]}
              localization={jp_JP as unknown as LocalizationMap}
            />
          </Worker>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BaseModalPreviewPdf;
